import { useState, useEffect, useRef } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { Employee, SupervisorFullDetailsDTO } from '../../axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';
import {
	Typography,
	Button,
	Box,
	Card,
	Checkbox,
	FormControlLabel,
	Autocomplete,
	TextField,
	Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSearch } from 'hooks/useSearch';
import SearchInput from 'components/SearchInput';
import { useNavigate } from 'react-router';
import EmployeeTable, { EmployeeTableColumns } from './EmployeeTable';


export type StateInput = {
	queryTerm?: string | null,
	sortValue?: string | null,
	orderValue?: string | null,
	filterValue?: null,
	currentPage?: number,
	clicked?: boolean,
};

export type SearchResult = {
	totalRecords?: number | null,
	employees?: Employee[]
};

const Employees = () => {
	const navigate = useNavigate();
	const api: Api = useApi();

	const [errors, setErrors] = useState({});
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [inactiveEmployees, setInactiveEmployees] = useState<boolean>(false);
	const [selectedSupervisor, setSelectedSupervisor] = useState<SupervisorFullDetailsDTO | null>(null);

	const toast = useToastMessageQueue();

	const [supervisors, setSupervisors] = useState<SupervisorFullDetailsDTO[]>([]);

	const fetchSupervisors = async () => {
		try {
			const response = await api.employeeApi.apiVversionEmployeeSupervisorsGet("1", undefined, true);
			if (response.data?.data) {
				setSupervisors(response.data.data);
			}
		} catch (error) {
			handleAPIError(error, toast, errors);
			setErrors({ ...errors });
		}
	};

	useEffect(() => {
		fetchSupervisors();
	}, []);

	const apiPromise = (
		searchTerm: string,
		pageNum: number,
		pageSize: number,
		sort: string,
		order: string
	) => {
		const filters = selectedSupervisor ? [`supervisor:${selectedSupervisor.id}`] : undefined;
		return api.employeeApi.apiVversionEmployeeSearchGet("1", searchTerm, pageNum, pageSize, sort, order, inactiveEmployees, filters);
	};

	const processError = (error: any) => {
		handleAPIError(error, toast, errors);
		setErrors({ ...errors });
	};

	// Encode volatile filter state as a stable string so useSearch re-fetches
	// when inactiveEmployees or selectedSupervisor changes — without needing
	// a separate reFetchData() call (which uses a stale apiPromise closure).
	const filterKey = `${inactiveEmployees}|${selectedSupervisor?.id ?? ''}`;

	const { data, loading, onPaginationModelChange, handleSortModelChange, paginationModel, setFilter } = useSearch<Employee>({
		apiPromise: apiPromise,
		searchTerm: searchTerm,
		defaultSort: { sort: 'Name', order: 'asc' },
		onError: processError,
		pageKey: 'employees',
		initialFilter: [filterKey],
	});

	// Keep useSearch's filter in sync when inactiveEmployees or selectedSupervisor changes.
	const isFilterFirstMount = useRef(true);
	useEffect(() => {
		if (isFilterFirstMount.current) {
			isFilterFirstMount.current = false;
			return;
		}
		setFilter([filterKey]);
	}, [filterKey]);

	const handleClearSearch = () => {
		setSearchTerm('');
	};

	const handleInactiveEmployees = () => {
		setInactiveEmployees(!inactiveEmployees);
	};

	return (
		<Card sx={{ m: 2 }}>
			<Box sx={{ p: 3 }}>
				<Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
					{intl.get('employee.header')}
				</Typography>
				<Box p={3}>
					<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
						<Box display={'flex'} alignItems={'center'}>
							<SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
							<Autocomplete
								id="supervisor-filter"
								options={supervisors}
								getOptionLabel={(option) => `${option.name} ${option.lastName}`}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								value={selectedSupervisor}
								onChange={(event, newValue) => setSelectedSupervisor(newValue)}
								sx={{ minWidth: 250, ml: 2 }}
								size="small"
								renderInput={(params) => (
									<TextField
										{...params}
										label={intl.get('employeeList.filter.supervisor') || 'Filter by Supervisor'}
										placeholder={intl.get('employeeList.filter.supervisorPlaceholder') || 'Select supervisor'}
									/>
								)}
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={inactiveEmployees}
										onChange={handleInactiveEmployees}
										sx={{ ml: 1 }}
									/>
								}
								label={intl.get('employeeList.checkbox.inactive')}
							/>
						</Box>
						<Button onClick={() => navigate(`/employee/new`)}>
							<AddIcon /> {intl.get('employee.new') ? intl.get('employee.new') : intl.get('button.new')}
						</Button>
					</Box>
					<Box style={{ width: '100%', display: 'flex' }}>
						{loading && <Spinner />}
						{data && (
							<EmployeeTable
								paginationMode='server'
								loading={loading}
								data={data.queryResult}
								totalRecords={data.totalRecords}
								paginationModel={paginationModel}
								onPaginationModelChange={onPaginationModelChange}
								handleSortModelChange={handleSortModelChange}
								columns={[
									EmployeeTableColumns.NAME,
									EmployeeTableColumns.LASTNAME,
									EmployeeTableColumns.SUPERVISOR,
									EmployeeTableColumns.PROVIDER,
									EmployeeTableColumns.EMAIL,
									EmployeeTableColumns.ACTIONS,
								]}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</Card>
	);
}

export default Employees;